import Echo from "laravel-echo";
import Pusher from "pusher-js";
import axios from "axios";

declare global {
	interface Window {
		Pusher: any;
		Echo: any;
	}
}

export default defineNuxtPlugin((nuxtApp) => {
	const pusherKey = import.meta.env.VITE_MIX_PUSHER_APP_KEY;

	if (!pusherKey) {
		console.warn("[WebSocket] VITE_MIX_PUSHER_APP_KEY is not set. Skipping Echo/Pusher initialization.");
		return;
	}

	const headers: Record<string, string> = {
		Accept: "application/json",
	};

	if (import.meta.env.VITE_BEARER_TOKEN) {
		headers.Authorization = `Bearer ${import.meta.env.VITE_BEARER_TOKEN}`;
	}

	window.Pusher = Pusher;
	window.Echo = new Echo({
		broadcaster: "pusher",
		key: pusherKey,
		cluster: import.meta.env.VITE_MIX_PUSHER_APP_CLUSTER ?? "mt1",
		wsHost: import.meta.env.VITE_MIX_PUSHER_HOST,
		wsPort: import.meta.env.VITE_MIX_PUSHER_PORT ?? 80,
		wssPort: import.meta.env.VITE_MIX_PUSHER_PORT ?? 443,
		forceTLS: (import.meta.env.VITE_MIX_PUSHER_SCHEME ?? "https") === "https",
		enabledTransports: ["ws", "wss"],
		disableStats: true,
		enableLogging: import.meta.env.VITE_APP_ENV !== "production",
		activityTimeout: 30000,
		pongTimeout: 5000,
		authorizer: (channel: any) => {
			return {
				authorize: (socketId: any, callback: any) => {
					axios
						.post(
							import.meta.env.VITE_PUSHER_AUTH,
							{
								socket_id: socketId,
								channel_name: channel.name,
							},
							{
								withCredentials: true,
								headers: headers,
							},
						)
						.then((response) => {
							callback(false, response.data);
						})
						.catch((error) => {
							callback(true, error);
						});
				},
			};
		},
	});

	window.Echo.connector.pusher.connection.bind("connected", () => {
		console.log("[WebSocket] Connected", new Date().toISOString());
	});

	window.Echo.connector.pusher.connection.bind("disconnected", () => {
		console.log("[WebSocket] Disconnected", new Date().toISOString());
	});

	window.Echo.connector.pusher.connection.bind("error", (error: any) => {
		console.error("[WebSocket] Connection error:", error, new Date().toISOString());
	});

	let reconnectTimer: number | null = null;

	window.Echo.connector.pusher.connection.bind("state_change", (states: any) => {
		const { previous, current } = states;
		console.log(`[WebSocket] State changed: ${previous} -> ${current}`);

		if (current === "disconnected" || current === "failed" || current === "unavailable") {
			if (reconnectTimer) clearTimeout(reconnectTimer);
			reconnectTimer = window.setTimeout(() => {
				console.log("[WebSocket] Attempting forced reconnection...");
				window.Echo.connector.pusher.connect();
			}, 1000);
		}

		if (current === "connected") {
			if (reconnectTimer) {
				clearTimeout(reconnectTimer);
				reconnectTimer = null;
			}
		}
	});

	nuxtApp.provide("echo", window.Echo);
});
